import { useContext, useEffect, useRef, type PropsWithChildren } from "react";
import { Redirect } from "wouter";

import {
  ADMIN_ACCESS_REDIRECT_REASON_PARAM,
  ADMIN_ACCESS_REDIRECT_REASONS,
  ADMIN_SIGN_IN_PATH,
  type AdminAccessRedirectReason,
} from "@/shared/routes/adminAccess";
import { AuthContext, type SessionPermissionId } from "@/shared/context/auth-context";
import { StatusPage } from "@/shared/layout/StatusPage";
import { logger } from "@/lib/logging/logger";

type ProtectedRouteProps = PropsWithChildren<{
  // The user must hold EVERY permission in this list (AND), at any scope.
  requiredPermissions?: SessionPermissionId[];
  // The user must hold EVERY permission in this list at GLOBAL scope (no agency/
  // brand/creator-network scope). Use for platform-wide admin surfaces whose
  // backend gate is `hasGlobalPermission` — a scoped grant must not open the route.
  requiredGlobalPermissions?: SessionPermissionId[];
  // The user must hold AT LEAST ONE permission from this list (OR). Used for
  // routes where multiple disjoint permissions can authorize access (e.g.
  // annotate posts is reachable by holders of either canReadPostAnnotations or
  // canReadPoliticalPostAnnotations). When combined with requiredPermissions,
  // both checks must pass: the user needs every required permission AND at
  // least one anyOf permission.
  anyOfPermissions?: SessionPermissionId[];
  // Like anyOfPermissions, but each grant must be at GLOBAL scope. Use for
  // routes whose backend gate is an OR of `hasGlobalPermission` checks (e.g.
  // the trace tools: global canManagePlatform OR canAnnotatePosts) — a scoped
  // grant must not open the route only to have the first query 403.
  anyOfGlobalPermissions?: SessionPermissionId[];
  // What happens when an unauthenticated user hits this route:
  // - "signin" (default): redirect to the marketing `/signin` page.
  // - "reinvite": render an in-place "re-request access" message instead.
  // Creator-facing routes use "reinvite" because creators have no email
  // sign-in — `/signin` is a dead end for them (it only offers a "book a
  // call" Calendly widget), so a lapsed session would strand them there.
  unauthenticatedFallback?: "signin" | "reinvite";
}>;

/**
 * Route guard. The three permission lists are ANDed together: the user must hold
 * EVERY `requiredPermissions` (at any scope) AND EVERY `requiredGlobalPermissions`
 * (at global scope only) AND — when `anyOfPermissions` is non-empty — AT LEAST ONE
 * of those (at any scope).
 *
 * Mixed scopes share one gate by necessity, not convenience: admin routes always
 * require `canAccessAdmin`, which is granted at varying scopes (agency-scoped for
 * `agencyManager`, etc.) and so must be checked at ANY scope, while a platform-wide
 * capability whose backend gate is `hasGlobalPermission` (e.g. `canAnnotatePosts`
 * for channel-stats proof review) must be checked at GLOBAL scope. A single route
 * needs both, so a separate "global-only" guard wouldn't remove the combination —
 * it would still have to re-accept the any-scope admin grant.
 */
export function ProtectedRoute({
  children,
  requiredPermissions = [],
  requiredGlobalPermissions = [],
  anyOfPermissions = [],
  anyOfGlobalPermissions = [],
  unauthenticatedFallback = "signin",
}: ProtectedRouteProps) {
  const { isLogged, isFetching, session, hasGlobalPermission } = useContext(AuthContext);
  const log = useRef(logger.child("ProtectedRoute"));
  const sessionPermissions = session?.permissions ?? [];
  const userPermissionIds = sessionPermissions.map((permission) => permission.permissionId.toString());

  const hasPermissionRequirement =
    requiredPermissions.length > 0 ||
    requiredGlobalPermissions.length > 0 ||
    anyOfPermissions.length > 0 ||
    anyOfGlobalPermissions.length > 0;
  const requiresAccessCheck = hasPermissionRequirement;
  const hasAllRequired = requiredPermissions.every((permissionId) => userPermissionIds.includes(permissionId));
  const hasAllGlobalRequired = requiredGlobalPermissions.every((permissionId) => hasGlobalPermission(permissionId));
  const hasAnyOfRequired =
    anyOfPermissions.length === 0 || anyOfPermissions.some((permissionId) => userPermissionIds.includes(permissionId));
  const hasAnyOfGlobalRequired =
    anyOfGlobalPermissions.length === 0 ||
    anyOfGlobalPermissions.some((permissionId) => hasGlobalPermission(permissionId));
  const hasRequiredPermissions = hasAllRequired && hasAllGlobalRequired && hasAnyOfRequired && hasAnyOfGlobalRequired;

  // When specific permissions are requested, the route requires every one of them.
  // Without explicit requirements we only need the user to be authenticated.
  const meetsAccessRequirements = !requiresAccessCheck || hasRequiredPermissions;

  // Prevent premature redirects by waiting until session data settles or confirms the requirements.
  const isInitialLoading = isFetching && (requiresAccessCheck ? !session : !isLogged);

  // Treat the route as admin-scoped (and redirect to the admin sign-in flow on
  // failure) when `canAccessAdmin` participates in EITHER gate. Routes that
  // accept several disjoint authorizations (e.g. global admin OR a scoped
  // role) put canAccessAdmin in `anyOfPermissions` rather than
  // `requiredPermissions` — they still belong in the admin URL space and the
  // redirect UX must match.
  const requiresAdminAccess =
    requiredPermissions.includes("canAccessAdmin") || anyOfPermissions.includes("canAccessAdmin");

  // Capture the specific admin failure reason so we can surface the appropriate message upstream.
  const adminRedirectReason: AdminAccessRedirectReason | undefined =
    requiresAdminAccess && !isInitialLoading
      ? !isLogged
        ? ADMIN_ACCESS_REDIRECT_REASONS.NOT_AUTHENTICATED
        : !meetsAccessRequirements
          ? ADMIN_ACCESS_REDIRECT_REASONS.INSUFFICIENT_PERMISSIONS
          : undefined
      : undefined;

  // An unauthenticated, non-admin visitor either gets redirected to the
  // marketing sign-in page or shown the in-place "re-request access" message,
  // depending on `unauthenticatedFallback`.
  const isUnauthenticatedNonAdmin = !requiresAdminAccess && !isInitialLoading && !isLogged;
  const shouldRedirectToSignIn = isUnauthenticatedNonAdmin && unauthenticatedFallback === "signin";
  const shouldShowReinvite = isUnauthenticatedNonAdmin && unauthenticatedFallback === "reinvite";

  // Diagnostics: when a creator lands here without a session, capture the
  // context so we can tell pure session-expiry apart from in-app-browser /
  // wallet-app handoffs that drop the cookie (see incident investigation).
  useEffect(() => {
    if (!shouldShowReinvite) {
      return;
    }

    log.current.warn("auth.reinviteRequired", {
      attributes: {
        path: typeof window === "undefined" ? undefined : window.location.pathname,
        referrer: typeof document === "undefined" ? undefined : document.referrer || undefined,
        userAgent: typeof navigator === "undefined" ? undefined : navigator.userAgent,
      },
    });
  }, [shouldShowReinvite]);

  if (isInitialLoading) {
    return (
      <StatusPage
        kind="loading"
        title={requiresAdminAccess ? "Checking admin access" : "Checking access"}
        subtitle={
          requiresAdminAccess
            ? "Please wait while we verify your permissions."
            : requiresAccessCheck
              ? "Please wait while we verify your access."
              : "Please wait while we verify your session."
        }
      />
    );
  }

  if (shouldShowReinvite) {
    return (
      <StatusPage
        kind="expired"
        title="Your session has expired"
        subtitle="The link expired, please request a new one in your Discord."
      />
    );
  }

  // Declarative redirect (applied during commit) instead of navigating from an
  // effect — this closes the render-then-effect gap where a protected view could
  // briefly render before the effect fired. `/signin` keeps history so a
  // post-login return lands back here; the admin sign-in redirect replaces the
  // entry and carries the failure reason.
  if (shouldRedirectToSignIn) {
    return <Redirect to="/signin" />;
  }

  if (adminRedirectReason) {
    const params = new URLSearchParams({
      [ADMIN_ACCESS_REDIRECT_REASON_PARAM]: adminRedirectReason,
    });
    return <Redirect to={`${ADMIN_SIGN_IN_PATH}?${params.toString()}`} replace />;
  }

  // When access requirements are present but unmet, block the route by rendering nothing.
  if (requiresAccessCheck && !meetsAccessRequirements) {
    return undefined;
  }

  // When no extra requirements are provided we still guard against anonymous users, rendering nothing until sign-in.
  if (!isLogged) {
    return undefined;
  }

  return <>{children}</>;
}
